import axios from "axios";
import { CredentialManager } from "../classes/credentialManager";
import { apiConfig } from "../config/authConfig";
import { IReportRegistration } from "../types/report";

export namespace ReportService {
	const credentialManager = CredentialManager.getInstance();

	/**********
	 * Convert one api response object to one ReportRegistration.
	 * @param data Batch response data from an API.
	 **********/
	export function mapReportRegistration(data: any) {
		let batch: IReportRegistration = {
			id: data.id,
			ordinal: data.ordinal,
			displayName: data.displayName,
			name: data.name,
			reportId: data.reportId,
			workspaceId: data.workspaceId,
			active: data.active,
			type: data.type,
			permission: data.permission

		}

		return batch;
	}

	/**********
	 * Convert an array of api response objects to an array of ReportRegistrations.
	 * @param data Batch data from an API response.
	 **********/
	export function mapReportRegistrations(data: any) {
		let reportRegistrations: IReportRegistration[] = [];

		if (Array.isArray(data) && data.length > 0) {
			data.map((item: any) => {
				reportRegistrations.push(mapReportRegistration(item));
			})
		}

		return reportRegistrations;
	}

	/**********
	 * Gets all report registrations.
	 **********/
	export async function getReportRegistrations() {
		const response = axios.get(
			apiConfig.default + "/report/register",
			{
				headers: {
					'Authorization': `Bearer ${await credentialManager.getAccessToken()}`
				},
			}
		)

		return response;
	}

	/**********
	 * Gets a report registration by ID.
	 **********/
	export async function getReportRegistration(id: string) {
		const response = axios.get(
			apiConfig.default + "/report/register/" + id,
			{
				headers: {
					'Authorization': `Bearer ${await credentialManager.getAccessToken()}`
				},
			}
		)

		return response;
	}

	/**********
	 * Register a new report.
	 * @param request The report registration details.
	 **********/
	export async function register(request: IReportRegistration) {
		const response = axios.post(
			apiConfig.default + "/report/register",
			request,
			{
				headers: {
					'Authorization': `Bearer ${await credentialManager.getAccessToken()}`
				},
			}
		)

		return response;
	}

	/**********
	 * Update a report registration's details.
	 * @param request The report registration details.	
	 **********/
	export async function setRegistration(request: IReportRegistration) {
		const response = axios.put(
			apiConfig.default + "/report/register",
			request,
			{
				headers: {
					'Authorization': `Bearer ${await credentialManager.getAccessToken()}`
				},
			}
		)

		return response;
	}

	/**********
	 * Unregister a report.
	 * @param id The report registration id.	
	 **********/
	export async function unregister(id: number) {
		const response = axios.delete(
			apiConfig.default + "/report/register/" + id,
			{
				headers: {
					'Authorization': `Bearer ${await credentialManager.getAccessToken()}`
				},
			}
		)

		return response;
	}

	/**********
	 * Change the position of a report in the menu.
	 * @param id The report registration id.	
	 * @param ordinal The new position's ordinal.
	 **********/
	export async function changeOrdinal(id: number, ordinal: number) {
		const response = axios.put(
			apiConfig.default + "/report/register/" + id + "/ordinal/" + ordinal,
			"",
			{
				headers: {
					'Authorization': `Bearer ${await credentialManager.getAccessToken()}`
				},
			}
		)

		return response;
	}

	/**********
	 * Gets embedded Power BI report information.
	 * @param reportId The ID of the report to retrieve.
	 * @param workspaceId (Optional) The ID of the workspace containing the report.
	 * @param type The type of the item to retrieve, either "report" or "dashboard".
	 * @param useRowLevelSecurity Whether row-level security identification will be required for this report.
	 **********/
	export async function getEmbeddedPowerBiReport(reportId: string, workspaceId?: string, type: "report" | "dashboard" = "report", useRowLevelSecurity: boolean = false) {	
		var data = {
			reportId: reportId,
			workspaceId: workspaceId,
			type: type,
			useRowLevelSecurity: useRowLevelSecurity
		}
		const apiResponse = axios.post(			
			apiConfig.default + "/report/epbi",
			data,
			{
				headers: {
					'Authorization': `Bearer ${await credentialManager.getAccessToken()}`
				},
			}
		)

		return apiResponse;
	}
}